import { createAsyncThunk } from "@reduxjs/toolkit";
import { ThunkApiConfig } from "@shared/models/redux";
import {
  RemoveContactAssignmentsApiResponse,
  RemoveContactAssignmentsRequest,
  RemoveContactAssignmentsResponse,
} from "@myProfile/models";
import { openNotificationThunk } from "@store/Notification/thunks";

// The backend serializes this response PascalCase (no [JsonProperty]/camelCase resolver), so read
// both casings and collapse to the canonical camelCase shape the rest of the app expects.
const normalizeRemovalResponse = (data: RemoveContactAssignmentsApiResponse): RemoveContactAssignmentsResponse => ({
  success: data.success ?? data.Success ?? false,
  message: data.message ?? data.Message ?? "",
  guid: data.guid ?? data.Guid ?? "",
});

/** POST /entitlements/v1/user/UserProfile/removeContactAssignments */
export const removeAccountFundAccessThunk = createAsyncThunk<
  RemoveContactAssignmentsResponse,
  RemoveContactAssignmentsRequest,
  ThunkApiConfig
>("myProfile/removeAccountFundAccess", async (request, { extra, dispatch, rejectWithValue }) => {
  try {
    const url = "/entitlements/v1/user/UserProfile/removeContactAssignments";
    const response = await extra<RemoveContactAssignmentsApiResponse, RemoveContactAssignmentsRequest>(url, {
      method: "POST",
      body: request,
    });

    if (!response || !response.data) {
      const message = "Failed to remove access. Please try again.";

      dispatch(openNotificationThunk({ variant: "warning", text: message }));

      return rejectWithValue(message);
    }

    const result = normalizeRemovalResponse(response.data);

    if (!result.success) {
      const message = result.message || "Failed to remove access. Please try again.";

      dispatch(openNotificationThunk({ variant: "warning", text: message }));

      return rejectWithValue(message);
    }

    dispatch(
      openNotificationThunk({
        variant: "info",
        text: result.message || "Your changes have been saved.",
      })
    );

    return result;
  } catch (error: unknown) {
    let errorMessage = "Failed to remove access. Please try again.";

    if (typeof error === "object" && error !== null && "response" in error) {
      const axiosError = error as { response?: { data?: { message?: string } } };

      errorMessage = axiosError.response?.data?.message || errorMessage;
    }

    dispatch(openNotificationThunk({ variant: "warning", text: errorMessage }));

    return rejectWithValue(errorMessage);
  }
});
